"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Section } from "@/components/ui/section"
import { Link, usePathname } from "i18n/routing"
import { CalendarDays, Mails, Phone } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { useRouter } from "next/navigation"
import LogoSvg from "public/assets/svg/common/LogoSvg"
import FacebookIcon from "public/assets/svg/Home/FacebookIcon"
import XIcon from "public/assets/svg/Home/XIcon"
import ReactCountryFlag from "react-country-flag"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "../ui/select"

const Footer = () => {
	const t = useTranslations()
	const locale = useLocale()

	const pathname = usePathname()
	const router = useRouter()

	return (
		<>
			<Section
				className="bg-gradient-to-b from-background from-50% to-muted to-50%"
				classNameContainer="max-w-screen-xl"
			>
				<Card className="bg-primary">
					<CardContent className="flex flex-col gap-16 p-8 lg:flex-row lg:items-center lg:grid lg:grid-cols-3">
						<div className="flex self-center">
							<Image
								src="/assets/svg/Home/logo_spallian.svg"
								alt="Logo - Spallian"
								priority
								height={150}
								width={190}
								className="self-center"
							/>
						</div>
						<div className="flex flex-col gap-4">
							<h3 className="text-center text-white">
								{t.rich(
									"footer.spallian_container_card.spallian_information_title",
									{
										br: () => <br></br>,
									}
								)}
							</h3>
							<Button
								variant="secondary"
								className="self-center"
								asChild
							>
								<Link
									locale={locale}
									href={"https://spallian.com"}
								>
									{"SPALLIAN.COM"}
								</Link>
							</Button>
						</div>
						<div className="flex items-center justify-center gap-8 lg:justify-end">
							<Link
								locale={locale}
								href={"https://linkedin.com/company/spallian"}
								target="_blank"
							>
								<Image
									src="/assets/svg/Home/linkedin.svg"
									alt="Social - Linkedin"
									width={32}
									height={32}
								/>
							</Link>
							<Link
								locale={locale}
								href={
									"https://youtube.com/channel/UCTGtvUe5uZgpCbeKgMFp_OA"
								}
								target="_blank"
							>
								<Image
									src="/assets/svg/Home/youtube.svg"
									alt="Social - Youtube"
									width={32}
									height={32}
								/>
							</Link>
							<Link
								locale={locale}
								href={"https://twitter.com/spallianfr"}
								target="_blank"
							>
								<XIcon className="fill-white ml-[-6px]" />
							</Link>
						</div>
					</CardContent>
				</Card>
			</Section>
			<Section
				className="bg-muted"
				classNameContainer="grid grid-cols-1 lg:grid-cols-3"
			>
				<div className="flex flex-col gap-6 md:gap-8">
					<h3 className="opacity-[80%]">
						{t("footer.link_container_1.link_title")}
					</h3>
					<div className="flex flex-col gap-4">
						<div className="flex flex-row items-center gap-4 opacity-[80%]">
							<div className="flex items-center justify-center p-2 border border-solid rounded-full border-foreground">
								<CalendarDays />
							</div>

							<p>
								{t(
									"footer.link_container_1.text_container_1.link_item"
								)}
							</p>
						</div>
						<Link
							locale={locale}
							href="tel:+33158393999"
							className="flex flex-row items-center gap-4 opacity-[80%]"
						>
							<div className="flex items-center justify-center p-2 border border-solid rounded-full border-foreground">
								<Phone />
							</div>

							<p>
								{t(
									"footer.link_container_1.text_container_2.link_item"
								)}
							</p>
						</Link>
						<Link
							locale={locale}
							href="mail:support@thelma.app"
							className="flex flex-row items-center gap-4 opacity-[80%]"
						>
							<div className="flex items-center justify-center p-2 border border-solid rounded-full border-foreground">
								<Mails />
							</div>

							<p>
								{t(
									"footer.link_container_1.text_container_3.link_item"
								)}
							</p>
						</Link>
					</div>
				</div>
				<div className="flex flex-col gap-6 md:gap-8">
					<h3 className="opacity-[80%]">
						{t("footer.link_container_2.link_title")}
					</h3>
					<div className="flex flex-col gap-4">
						<Link locale={locale} href={"/mentions-legales"}>
							<p className="opacity-[80%]">
								{t("footer.link_container_2.link_item_1")}
							</p>
						</Link>
						<Link
							locale={locale}
							href={"/conditions-generales-utilisation"}
						>
							<p className="opacity-[80%]">
								{t("footer.link_container_2.link_item_2")}
							</p>
						</Link>
						<Link
							locale={locale}
							href={"/politique-de-donnees-personnelles"}
						>
							<p className="opacity-[80%]">
								{t("footer.link_container_2.link_item_3")}
							</p>
						</Link>
						<Link
							locale={locale}
							href={"/politique-de-confidentialite"}
						>
							<p className="opacity-[80%]">
								{t("footer.link_container_2.data_privacy")}
							</p>
						</Link>
						<Link locale={locale} href={"/faq"}>
							<p className="opacity-[80%]">
								{t("footer.link_container_2.link_item_4")}
							</p>
						</Link>
					</div>
				</div>
				<div className="flex flex-col gap-6 md:gap-8">
					<h3 className="opacity-[80%]">
						{t("footer.link_container_3.link_title")}
					</h3>
					<div className="flex gap-6">
						<Link
							locale={locale}
							href={"https://www.facebook.com/thelmaapp"}
							target="_blank"
						>
							<FacebookIcon className="fill-foreground" />
						</Link>
						<Link
							locale={locale}
							href={"https://twitter.com/spallianfr"}
							target="_blank"
						>
							<XIcon className="fill-foreground ml-[-6px]" />
						</Link>
					</div>
				</div>
			</Section>
			<Section className="bg-muted " classNameContainer="pt-0">
				<hr className="border-t-0 h-[1px] bg-border" />
				<div className="flex flex-col items-start justify-between gap-8 lg:items-center lg:flex-row">
					<LogoSvg
						className="w-[220px]"
						classNameText="dark:fill-foreground"
					/>
					<p className="lg:text-end">{t("footer.copyright_text")}</p>
				</div>

				<div className="flex items-center self-end gap-4">
					<Select
						defaultValue={locale}
						onValueChange={(value) => {
							const pathnameWithoutLocale = pathname.replace(
								/^\/(fr|en)/,
								""
							)

							router.replace(
								`/${value}${pathnameWithoutLocale}`,
								{
									locale: value,
								}
							)
							router.refresh()
						}}
					>
						<SelectTrigger className="w-[105px]">
							<SelectValue placeholder="Langues" />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								<SelectLabel>Langues</SelectLabel>
								<SelectItem value="fr">
									<>
										<ReactCountryFlag
											countryCode="FR"
											className="mr-2"
										/>
										<span>FR</span>
									</>
								</SelectItem>
								<SelectItem value="en">
									<>
										<ReactCountryFlag
											countryCode="US"
											className="mr-2"
										/>
										<span>EN</span>
									</>
								</SelectItem>
							</SelectGroup>
						</SelectContent>
					</Select>
				</div>
			</Section>
		</>
	)
}

export default Footer
